import { writable } from 'svelte/store'

export type ToastType = 'success' | 'error' | 'loading'

export interface Toast {
	id: string
	message: string
	type: ToastType
	timeoutId?: number
}

function createToastStore() {
	const { subscribe, update } = writable<Toast[]>([])
	const SUCCESS_TIMEOUT = 5000 // 5 seconds

	// Helper to generate unique IDs for toasts
	const generateId = () => Math.random().toString(36).substring(2, 9)

	// Helper to clear timeout if it exists
	const clearToastTimeout = (toast: Toast) => {
		if (toast.timeoutId) {
			window.clearTimeout(toast.timeoutId)
		}
	}

	// Create store methods object with proper binding
	const methods = {
		subscribe,

		/**
		 * Add a new toast notification
		 * Returns the ID of the created toast
		 */
		add: (toast: Omit<Toast, 'id'>): string => {
			const id = generateId()

			update((toasts) => {
				const newToast = { ...toast, id }

				// Set auto-dismiss timeout for success toasts
				if (toast.type === 'success') {
					// Store the remove method reference to prevent context loss
					const removeToast = methods.remove
					newToast.timeoutId = window.setTimeout(() => {
						removeToast(id)
					}, SUCCESS_TIMEOUT)
				}

				return [...toasts, newToast]
			})

			return id
		},

		/**
		 * Update an existing toast by ID
		 * Will clear any existing timeout and set a new one if type is success
		 */
		update: (id: string, newToast: Partial<Omit<Toast, 'id'>>) => {
			update((toasts) => {
				return toasts.map((toast) => {
					if (toast.id === id) {
						// Clear existing timeout
						clearToastTimeout(toast)

						const updatedToast = { ...toast, ...newToast }

						// Set new timeout if type is success
						if (updatedToast.type === 'success') {
							// Store the remove method reference to prevent context loss
							const removeToast = methods.remove
							updatedToast.timeoutId = window.setTimeout(() => {
								removeToast(id)
							}, SUCCESS_TIMEOUT)
						}

						return updatedToast
					}
					return toast
				})
			})
		},

		/**
		 * Remove a toast by ID
		 * Ensures timeout is cleared
		 */
		remove: (id: string) => {
			update((toasts) => {
				const toastToRemove = toasts.find((t) => t.id === id)
				if (toastToRemove) {
					clearToastTimeout(toastToRemove)
				}
				return toasts.filter((t) => t.id !== id)
			})
		},

		/**
		 * Clear all toasts
		 * Ensures all timeouts are cleared
		 */
		clear: () => {
			update((toasts) => {
				toasts.forEach(clearToastTimeout)
				return []
			})
		},
	}

	return methods
}

export const toastStore = createToastStore()
